import React, { useMemo } from 'react';
import { Button, Space, Table, Typography } from 'antd';

const isCompletedStatus = (status) => String(status || '').toLowerCase() === 'completed';

const parseJobDate = (job) => {
  const raw = job?.selectedDateString || job?.date;
  if (!raw) return null;

  if (typeof raw === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(raw)) {
    const [dd, mm, yyyy] = raw.split('-');
    const parsed = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const parseJobDateTime = (job) => {
  const baseDate = parseJobDate(job);
  if (!baseDate) return null;

  const slots = Array.isArray(job?.slots) ? job.slots : [];
  const timeText = slots[0] || job?.starttime || job?.time;

  if (!timeText || typeof timeText !== 'string') {
    return new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), 0, 0, 0, 0);
  }

  const match = timeText.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), 0, 0, 0, 0);
  }

  const hour = Number(match[1]);
  const minute = Number(match[2]);
  return new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), hour, minute, 0, 0);
};

export default function OverdueAtRiskView({ jobs, columns, loading, onRefresh }) {
  const { overdue, atRisk } = useMemo(() => {
    const now = new Date();
    const in24h = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    const in48h = new Date(now.getTime() + 48 * 60 * 60 * 1000);

    const overdueRows = [];
    const atRiskRows = [];

    jobs.forEach((job) => {
      if (isCompletedStatus(job?.status)) return;

      const dueAt = parseJobDateTime(job);
      if (!dueAt) return;

      if (dueAt < now) {
        overdueRows.push(job);
        return;
      }

      if (dueAt >= in24h && dueAt <= in48h) {
        atRiskRows.push(job);
      }
    });

    const sortBySoonest = (a, b) => {
      const aDate = parseJobDateTime(a);
      const bDate = parseJobDateTime(b);
      if (aDate && bDate) return aDate.getTime() - bDate.getTime();
      if (aDate) return -1;
      if (bDate) return 1;
      return 0;
    };

    return {
      overdue: overdueRows.sort(sortBySoonest),
      atRisk: atRiskRows.sort(sortBySoonest),
    };
  }, [jobs]);

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={16}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Typography.Text strong>
          Overdue ({overdue.length}) • At Risk next 24–48h ({atRisk.length})
        </Typography.Text>
        <Button onClick={onRefresh} loading={loading}>
          Refresh
        </Button>
      </Space>

      <div>
        <Typography.Title level={5} style={{ marginTop: 0 }}>
          Overdue
        </Typography.Title>
        <Table
          columns={columns}
          dataSource={overdue}
          loading={loading}
          pagination={false}
          scroll={{ x: 900 }}
          locale={{ emptyText: 'No overdue jobs' }}
        />
      </div>

      <div>
        <Typography.Title level={5} style={{ marginTop: 0 }}>
          At Risk (Due in next 24–48 hours)
        </Typography.Title>
        <Table
          columns={columns}
          dataSource={atRisk}
          loading={loading}
          pagination={false}
          scroll={{ x: 900 }}
          locale={{ emptyText: 'No at-risk jobs in next 24–48 hours' }}
        />
      </div>
    </Space>
  );
}
